// ── src/lib/packages.js ────────────────────────────────────────────────────
// Fuente de verdad de categorías y paquetes de cotización.
// visible: false → no aparece en el landing del cliente (admin puede ocultar).
// featured: true → se muestra primero con badge "Plan recomendado".
// El orden en el array define el orden de aparición en el landing.

// ── CONFIGURACIÓN DEL ESTUDIO ─────────────────────────────────────────────
// Solo uso interno (buildWALink); nadie lo importa. Hardcode Feria pendiente
// de configuracion por tenant (Fase 8).
const ESTUDIO_WHATSAPP = '593999948096'; // Sin + ni espacios

// ── PRECIOS POR MERCADO ───────────────────────────────────────────────────
// nacional     = Ecuador · precios en USD
// internacional = LATAM · USA · Europa · precios más altos (mercado global)
export const PRECIOS_INTERNACIONAL = {
  normal_starter:      2000,
  normal_select:       3600,
  normal_premium:      5600,
  corporate_custom:   18000,
  corporate_web:      18000,
  corporate_essence:   7787,
  corporate_legacy:    9576,
  corporate_ascend:   12312,
  // Legacy ids kept as fallback for old links without package snapshots.
  branding_starter:    1579,
  branding_identity:   3052,
  branding_legacy:     4841,
};
export const PACKAGE_CONFIG_STORAGE_KEY = 'feria_package_config_v1';

/**
 * LOS CAMPOS COMERCIALES QUE EL NAVEGADOR NO PUEDE DECIDIR.
 *
 * `loadPackageConfig` mezcla lo guardado en `localStorage` sobre la tabla del codigo, y esa
 * mezcla ES `PACKAGE_RULES`. El precio ya no se lee de ahi —cero lecturas, medido—, pero el
 * CUPO si: `Kanban` hace `isExtra = index >= rule.includedApplications`, que es una decision
 * de cobro. Un numero guardado en un navegador no puede decidir si una aplicacion se cobra.
 *
 * La autoridad del cupo es `paquetes_cotizacion.application_count`; la del precio, la misma
 * tabla. Esto no las sustituye: impide que el navegador las contradiga.
 */
export const CAMPOS_SIN_AUTORIDAD_LOCAL = Object.freeze([
  'includedApplications',
  'packageAppLimit',
  'price',
  'price_respaldo',
  'precio',
]);

/** Lo guardado, sin los campos comerciales. Se aplica a CADA regla, una a una. */
export function reglaSinAutoridadComercial(regla) {
  if (!regla || typeof regla !== 'object') return {};
  const limpia = {};
  Object.keys(regla).forEach((clave) => {
    if (CAMPOS_SIN_AUTORIDAD_LOCAL.includes(clave)) return;
    limpia[clave] = regla[clave];
  });
  return limpia;
}
export const PACKAGE_VISIBLE_DELIVERABLES = {
  normal_starter: [
    'Diseño de logotipo principal',
    'Variaciones básicas del logotipo',
    'Selección de tipografía complementaria',
    'Diseño de gráfica complementaria',
    'Paleta cromática de marca',
    'Guía esencial de marca',
    'Usos correctos e incorrectos del logotipo',
    'Fondos permitidos',
    'Tamaños mínimos de uso',
    'Aplicaciones incluidas en el paquete',
  ],
  // La progresion tipografica es de NIVEL, y Select se quedaba con los dos
  // peldanos a la vez: «Selección tipográfica de marca» -elegir las
  // tipografias de la marca- y «Sistema tipográfico de marca» -el sistema
  // completo, que es de Premium-. Prometia dos veces lo mismo, inflaba la
  // cuenta a 16 y contradecia a la matriz educativa, que ya daba 15.
  //
  //   Starter   Selección de tipografía complementaria
  //   Select    Selección tipográfica de marca
  //   Premium   Sistema tipográfico de marca
  //
  // Corregido por decision del owner el 2026-09-24. Las propuestas ya
  // enviadas conservan su instantanea: esto solo gobierna autoria nueva.
  normal_select: [
    'Diseño de logotipo principal',
    'Variaciones del logotipo',
    'Selección tipográfica de marca',
    'Diseño de gráfica complementaria',
    'Paleta cromática principal y secundaria',
    'Manual de marca',
    'Concepto visual de la marca',
    'Características y significado de la forma',
    'Proporciones y criterios de construcción',
    'Usos correctos e incorrectos del logotipo',
    'Fondos permitidos',
    'Tamaños mínimos de uso',
    'Combinaciones de color',
    'Criterios de composición visual',
    'Aplicaciones incluidas en el paquete',
  ],
  normal_premium: [
    'Diseño de logotipo principal',
    'Variaciones completas del logotipo',
    'Sistema tipográfico de marca',
    'Paleta cromática principal y secundaria',
    'Diseño de gráfica complementaria avanzada',
    'Dirección visual de marca',
    'Manual de identidad visual avanzado',
    'Concepto visual y fundamentos de la marca',
    'Características y significado de la forma',
    'Proporciones, construcción y criterios de uso',
    'Usos correctos e incorrectos del logotipo',
    'Fondos permitidos',
    'Tamaños mínimos de uso',
    'Combinaciones de color',
    'Criterios de composición visual',
    'Lineamientos de estilo gráfico',
    'Selección o dirección de estilo fotográfico',
    'Criterios para uso de imágenes',
    'Tono visual de marca',
    'Aplicaciones incluidas en el paquete',
  ],
};

export const PACKAGE_INTERNAL_PROCESS = {
  normal_starter: [
    'Lectura de brief e información recibida',
    'Revisión visual inicial',
    'Exploración de referencias internas',
  ],
  normal_select: [
    'Análisis de información recibida',
    'Estudio cromático',
    'Estudio tipográfico',
    'Estudio morfológico',
    'Revisión de coherencia visual',
  ],
  normal_premium: [
    'Análisis de información recibida',
    'Estudio cromático',
    'Estudio tipográfico',
    'Estudio morfológico',
    'Dirección creativa interna',
    'Revisión de estilo fotográfico',
  ],
};

export const PACKAGE_CLIENT_SUMMARIES = {
  normal_starter: {
    title: 'Starter - Identidad esencial',
    document: 'Guía esencial de marca',
    summary: 'Sistema esencial de logotipo, color, tipografía, gráfica complementaria y aplicaciones incluidas.',
  },
  normal_select: {
    title: 'Select - Identidad profesional',
    document: 'Manual de marca',
    summary: 'Sistema profesional de logotipo, color, tipografía, gráfica complementaria, criterios visuales y aplicaciones incluidas.',
  },
  normal_premium: {
    title: 'Premium - Sistema visual avanzado',
    document: 'Manual de identidad visual avanzado',
    summary: 'Sistema completo de logotipo, color, tipografía, gráfica complementaria, dirección visual, imagen y aplicaciones incluidas.',
  },
};

const PACKAGE_VISIBLE_DELIVERABLE_ALIASES = {
  branding_starter: 'normal_starter',
  branding_identity: 'normal_select',
  branding_legacy: 'normal_premium',
};

export function visibleDeliverablesForPackageId(packageId = '', fallback = []) {
  const key = String(packageId || '').trim();
  const direct = PACKAGE_VISIBLE_DELIVERABLES[key] || PACKAGE_VISIBLE_DELIVERABLES[PACKAGE_VISIBLE_DELIVERABLE_ALIASES[key]];
  return Array.isArray(direct) && direct.length ? direct : fallback;
}
export const CORPORATE_PACKAGE_NOTE = 'Todos incluyen auditoría de marca y animación de logo';

function readPackageConfig(defaultApplications, defaultRules) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return { applications: defaultApplications, rules: defaultRules };
  }

  try {
    const saved = JSON.parse(window.localStorage.getItem(PACKAGE_CONFIG_STORAGE_KEY) || 'null');
    if (!saved) return { applications: defaultApplications, rules: defaultRules };

    const savedApps = Array.isArray(saved.applications) ? saved.applications : [];
    const appsById = new Map(savedApps.map(app => [app.id, app]));
    const applications = [
      ...defaultApplications.map(app => ({ ...app, ...(appsById.get(app.id) || {}) })),
      ...savedApps.filter(app => app?.id && !defaultApplications.some(def => def.id === app.id)),
    ];

    // Lo guardado entra SIN sus campos comerciales: el navegador no decide cupo ni precio.
    const savedRulesBrutas = saved.rules && typeof saved.rules === 'object' ? saved.rules : {};
    const savedRules = Object.fromEntries(
      Object.entries(savedRulesBrutas).map(([name, rule]) => [name, reglaSinAutoridadComercial(rule)])
    );
    const rules = Object.fromEntries(
      Object.entries(defaultRules).map(([name, rule]) => [name, { ...rule, ...(savedRules[name] || {}) }])
    );

    // Un paquete que SOLO existe en lo guardado tampoco trae cupo ni precio: sin eso no es
    // un paquete vendible, y que no lo sea es la respuesta correcta.
    Object.entries(savedRules).forEach(([name, rule]) => {
      if (!rules[name]) rules[name] = rule;
    });

    Object.entries(defaultRules).forEach(([name, defaultRule]) => {
      const defaultCount = Number(defaultRule.includedApplications) || 0;
      const defaultSuggestedIds = Array.isArray(defaultRule.suggestedApplicationIds)
        ? defaultRule.suggestedApplicationIds
        : [];
      const currentCount = Number(rules[name]?.includedApplications) || 0;
      const legacyCount = (name === 'B&L Signature' && currentCount === 6)
        || (name === 'B&L Legacy' && currentCount === 8);
      if (defaultCount > 0 && (currentCount === 0 || legacyCount)) {
        rules[name] = {
          ...rules[name],
          includedApplications: defaultCount,
          suggestedApplicationIds: Array.isArray(rules[name]?.suggestedApplicationIds) && legacyCount
            ? rules[name].suggestedApplicationIds.slice(0, defaultCount)
            : rules[name]?.suggestedApplicationIds,
        };
      }
      const includedCount = Number(rules[name]?.includedApplications) || 0;
      const currentSuggestedIds = Array.isArray(rules[name]?.suggestedApplicationIds)
        ? rules[name].suggestedApplicationIds
        : [];
      if (includedCount > 0 && defaultSuggestedIds.length && !currentSuggestedIds.length) {
        rules[name] = {
          ...rules[name],
          suggestedApplicationIds: defaultSuggestedIds.slice(0, includedCount),
        };
      } else if (includedCount > 0 && currentSuggestedIds.length) {
        rules[name] = {
          ...rules[name],
          suggestedApplicationIds: currentSuggestedIds.slice(0, includedCount),
        };
      }
    });

    return { applications, rules };
  } catch {
    return { applications: defaultApplications, rules: defaultRules };
  }
}

// ── CATEGORÍAS DE SERVICIO ────────────────────────────────────────────────
export const CATEGORIAS = [
  {
    id:          'normal',
    nombre:      'Paquetes normales',
    subtitulo:   'Starter, Select y Premium',
    brand:       'feria',
    color:       'var(--gold)',
    icono:       '◈',
    orden:       1,
  },
  {
    id:          'corporate',
    nombre:      'Paquetes corporativos',
    subtitulo:   'Essence, Legacy y Ascend',
    brand:       'feria',
    color:       'var(--pink)',
    icono:       '◆',
    orden:       2,
  },
];

// ── PAQUETES POR CATEGORÍA ────────────────────────────────────────────────
export const PAQUETES = [

  // ── PAQUETES NORMALES ───────────────────────────────────────────────
  {
    id:        'normal_starter',
    categoria: 'normal',
    family:    'normal',
    nombre:    'Starter',
    subtitulo: 'Para marcas que inician con bases sólidas',
    precio:    1600,
    duracion:  '15 días hábiles',
    applicationCount: 3,
    visible:   true,
    featured:  false,
    orden:     3,
    documentIncluded: PACKAGE_CLIENT_SUMMARIES.normal_starter.document,
    clientSummary: PACKAGE_CLIENT_SUMMARIES.normal_starter.summary,
    internalProcess: PACKAGE_INTERNAL_PROCESS.normal_starter,
    items: PACKAGE_VISIBLE_DELIVERABLES.normal_starter,
  },
  {
    id:        'normal_select',
    categoria: 'normal',
    family:    'normal',
    nombre:    'Select',
    subtitulo: 'Para marcas que buscan profesionalizar su identidad',
    precio:    3000,
    duracion:  '20 días hábiles',
    applicationCount: 5,
    visible:   true,
    featured:  true,
    orden:     2,
    documentIncluded: PACKAGE_CLIENT_SUMMARIES.normal_select.document,
    clientSummary: PACKAGE_CLIENT_SUMMARIES.normal_select.summary,
    internalProcess: PACKAGE_INTERNAL_PROCESS.normal_select,
    items: PACKAGE_VISIBLE_DELIVERABLES.normal_select,
  },
  {
    id:        'normal_premium',
    categoria: 'normal',
    family:    'normal',
    nombre:    'Premium',
    subtitulo: 'El sistema de identidad más avanzado para una marca',
    precio:    4800,
    duracion:  '30 días hábiles',
    applicationCount: 10,
    visible:   true,
    featured:  false,
    orden:     1,
    documentIncluded: PACKAGE_CLIENT_SUMMARIES.normal_premium.document,
    clientSummary: PACKAGE_CLIENT_SUMMARIES.normal_premium.summary,
    internalProcess: PACKAGE_INTERNAL_PROCESS.normal_premium,
    items: PACKAGE_VISIBLE_DELIVERABLES.normal_premium,
  },

  // ── PAQUETES CORPORATIVOS ───────────────────────────────────────────
  {
    id:        'corporate_essence',
    categoria: 'corporate',
    family:    'corporate',
    nombre:    'Essence',
    subtitulo: 'Sistema corporativo base con auditoría y animación',
    precio:    7400,
    applicationCount: 3,
    duracion:  '30 días hábiles',
    corporateNote: CORPORATE_PACKAGE_NOTE,
    visible:   true,
    featured:  false,
    orden:     3,
    items: [
      'Auditoría de marca',
      'Animación de logo',
      'Sistema de identidad corporativa',
      'Manual de marca esencial',
      'Kit base de aplicaciones corporativas',
    ],
  },
  {
    id:        'corporate_legacy',
    categoria: 'corporate',
    family:    'corporate',
    nombre:    'Legacy',
    subtitulo: 'Sistema corporativo avanzado con estrategia y despliegue',
    precio:    9100,
    applicationCount: 5,
    duracion:  '40 días hábiles',
    corporateNote: CORPORATE_PACKAGE_NOTE,
    visible:   true,
    featured:  true,
    orden:     2,
    items: [
      'Todo lo incluido en Essence',
      'Arquitectura visual corporativa',
      'Sistema extendido de aplicaciones',
      'Manual de marca avanzado',
      'Lineamientos de implementación interna',
    ],
  },
  {
    id:        'corporate_ascend',
    categoria: 'corporate',
    family:    'corporate',
    nombre:    'Ascend',
    subtitulo: 'El nivel corporativo más completo para expansión',
    precio:    11700,
    applicationCount: 10,
    duracion:  '55 días hábiles',
    corporateNote: CORPORATE_PACKAGE_NOTE,
    visible:   true,
    featured:  false,
    orden:     1,
    items: [
      'Todo lo incluido en Legacy',
      'Sistema corporativo multiformato',
      'Dirección de implementación de marca',
      'Guía de consistencia para equipos',
      'Acompañamiento estratégico de despliegue',
    ],
  },
];

// ── HELPERS ───────────────────────────────────────────────────────────────

export function normalizePackageCategoryId(categoriaId = 'normal') {
  const raw = String(categoriaId || '').trim();
  if (raw === 'branding' || raw === 'arte') return 'normal';
  if (raw === 'bl') return 'corporate';
  return raw || 'normal';
}

export function isNationalMarket(mercado = '') {
  return String(mercado || '').toLowerCase() === 'nacional';
}

/** Tasa de IVA nacional (Ecuador). El "+ IVA" deja de ser texto: se materializa en el monto. */
export const IVA_RATE = 0.15;

/**
 * Bruto a cobrar/transferir según la regla nacional. Nacional = base × (1 + IVA); internacional = base.
 * ÚNICO lugar de verdad del ×1.15 en el frontend (su espejo Deno vive en las edge fns).
 * Los totales GUARDADOS quedan en NETO (convención migración 158); el bruto se realiza aquí y en el cobro.
 * Recibe el flag `esNacional` (columna cotizaciones_enviadas.es_nacional), no re-deriva de mercado.
 */
export function grossForNational(base = 0, esNacional = false) {
  const amount = Number(base || 0);
  const gross = esNacional ? amount * (1 + IVA_RATE) : amount;
  return Math.round(gross * 100) / 100;
}

export function formatPackageAmount(value = 0, mercado = 'internacional') {
  const locale = isNationalMarket(mercado) ? 'es-EC' : 'en-US';
  return `$${Number(value || 0).toLocaleString(locale)}`;
}

export function formatPackagePrice(value = 0, mercado = 'internacional', { includeUsd = true } = {}) {
  const suffix = isNationalMarket(mercado)
    ? `${includeUsd ? ' USD' : ''} + IVA`
    : `${includeUsd ? ' USD' : ''}`;
  return `${formatPackageAmount(value, mercado)}${suffix}`;
}

/** Precio según mercado — 'nacional' | 'internacional' */
export function getPrecio(paqueteId, mercado = 'internacional') {
  if (mercado === 'nacional') {
    const pkg = PAQUETES.find(p => p.id === paqueteId);
    return pkg?.precio || 0;
  }
  return PRECIOS_INTERNACIONAL[paqueteId] || PAQUETES.find(p => p.id === paqueteId)?.precio || 0;
}

/** Paquetes de una categoría, ordenados, opcionalmente solo los visibles */
export function paquetesPorCategoria(categoriaId, soloVisibles = false) {
  const normalizedCategoryId = normalizePackageCategoryId(categoriaId);
  return PAQUETES
    .filter(p => p.categoria === normalizedCategoryId && (!soloVisibles || p.visible))
    .sort((a, b) => a.orden - b.orden);
}

/** Categoría por id */
export function categoriaById(id) {
  const normalizedCategoryId = normalizePackageCategoryId(id);
  return CATEGORIAS.find(c => c.id === normalizedCategoryId) || CATEGORIAS[0];
}

/** Paquete por id */
export function paqueteById(id) {
  return PAQUETES.find(p => p.id === id);
}

/** Genera el link de WhatsApp pre-escrito para un paquete */
export function buildWALink(paquete, clienteNombre = '', studioName = 'Feria OS') {
  const brandName = String(studioName || 'Feria OS').trim();
  const saludo = clienteNombre ? `Hola Feria 👋\n\n` : `Hola Feria 👋\n\n`;
  const priceText = formatPackagePrice(paquete.precio, paquete.mercado || paquete.market || 'internacional');
  const msg = `${saludo}Acabo de ver la propuesta de *${brandName}* y me interesa el plan *${paquete.nombre}* (${priceText}).\n\n¿Podemos conversar?`;
  const consultativeMsg = msg ? `${saludo}Acabo de ver la propuesta de *${brandName}* y me interesa conversar sobre el plan *${paquete.nombre}* (${priceText}).\n\nMe gustaría aclarar dudas antes de confirmar el paquete final.` : msg;
  return `https://wa.me/${ESTUDIO_WHATSAPP}?text=${encodeURIComponent(consultativeMsg.replace('Hola Feria', `Hola ${brandName}`))}`;
}

// ── COMPAT: PAQUETES_BASE (alias para código existente) ──────────────────
// Mantiene compatibilidad con el código actual de Cotizaciones que usa PAQUETES_BASE.
export const PAQUETES_BASE = PAQUETES.map(p => ({
  id:       p.id,
  nombre:   p.nombre,
  precio:   p.precio,
  duracion: p.duracion,
  brand:    CATEGORIAS.find(c => c.id === p.categoria)?.brand || 'feria',
  color:    CATEGORIAS.find(c => c.id === p.categoria)?.color || 'var(--gold)',
  subtitulo: p.subtitulo,
  featured: p.featured,
  visible:  p.visible,
  categoria: p.categoria,
  family: p.family,
  applicationCount: p.applicationCount,
  corporateNote: p.corporateNote,
  documentIncluded: p.documentIncluded,
  clientSummary: p.clientSummary,
  internalProcess: p.internalProcess,
  items:    p.items,
}));

// ── APPLICATION CATALOG (sin cambios) ────────────────────────────────────
export const DEFAULT_APPLICATION_CATALOG = [
  { id: 'logo_principal',         name: 'Logotipo principal',               category: 'Identidad',   basePrice: 0   },
  { id: 'variaciones_marca',      name: 'Variaciones de marca',             category: 'Identidad',   basePrice: 0   },
  { id: 'manual_marca',           name: 'Manual de marca',                  category: 'Identidad',   basePrice: 0   },
  { id: 'tarjeta_presentacion',   name: 'Tarjeta de presentación',          category: 'Papelería',   basePrice: 80  },
  { id: 'firma_correo',           name: 'Firma de correo',                  category: 'Papelería',   basePrice: 60  },
  { id: 'hoja_membretada',        name: 'Hoja membretada',                  category: 'Papelería',   basePrice: 75  },
  { id: 'carpeta_corporativa',    name: 'Carpeta corporativa',              category: 'Papelería',   basePrice: 120 },
  { id: 'plantilla_post',         name: 'Plantilla de post Instagram',      category: 'Social',      basePrice: 95  },
  { id: 'plantilla_story',        name: 'Plantilla de historia Instagram',  category: 'Social',      basePrice: 75  },
  { id: 'portada_destacados',     name: 'Portadas de destacados',           category: 'Social',      basePrice: 70  },
  { id: 'presentacion_comercial', name: 'Presentación comercial',           category: 'Comercial',   basePrice: 220 },
  { id: 'brochure',               name: 'Brochure / dossier',               category: 'Comercial',   basePrice: 240 },
  { id: 'landing_visual',         name: 'Landing page visual',              category: 'Digital',     basePrice: 280 },
  { id: 'empaque',                name: 'Diseño de empaque',                category: 'Producto',    basePrice: 320 },
  { id: 'etiqueta',               name: 'Etiqueta de producto',             category: 'Producto',    basePrice: 180 },
  { id: 'menu',                   name: 'Menú / carta',                     category: 'Gastronomía', basePrice: 180 },
  { id: 'uniforme',               name: 'Aplicación en uniforme',           category: 'Experiencia', basePrice: 150 },
  { id: 'senaletica',             name: 'Señalética básica',                category: 'Experiencia', basePrice: 220 },
  { id: 'sticker',                name: 'Sticker / sello',                  category: 'Experiencia', basePrice: 65  },
  { id: 'watermark',              name: 'Watermark de marca',               category: 'Fotografía',  basePrice: 60  },
  { id: 'preset_lightroom',       name: 'Preset Lightroom de marca',        category: 'Fotografía',  basePrice: 120 },
  { id: 'portafolio_digital',     name: 'Portafolio digital (layout)',      category: 'Fotografía',  basePrice: 280 },
];

const BRANDING_APPLICATIONS_3 = [
  'tarjeta_presentacion',
  'firma_correo',
  'plantilla_post',
];

const BRANDING_APPLICATIONS_5 = [
  'tarjeta_presentacion',
  'firma_correo',
  'hoja_membretada',
  'plantilla_post',
  'plantilla_story',
];

const BRANDING_APPLICATIONS_10 = [
  'tarjeta_presentacion',
  'firma_correo',
  'hoja_membretada',
  'carpeta_corporativa',
  'plantilla_post',
  'plantilla_story',
  'portada_destacados',
  'presentacion_comercial',
  'brochure',
  'landing_visual',
];

// ── ESTA TABLA YA NO ES AUTORIDAD DE PRECIO ─────────────────────────
//
// `price` se llama ahora `price_respaldo`, y el cambio de nombre ES la guarda: un `.price`
// que no existe no se puede leer por descuido. Los numeros de aqui son ANTERIORES al
// Pricebook V1 — 1500 / 2900 / 4600 frente a los canonicos 1600 / 3000 / 4800 — y nada los
// reconciliaba contra `paquetes_cotizacion`.
//
// La autoridad monetaria de un paquete es `paquetes_cotizacion`, leida por
// `src/lib/precioDePaquete.js`. Lo que este respaldo sigue aportando —y para lo que un
// respaldo sirve— es ESTRUCTURA: nombres, cupos, plazos y el servicio por omision.
//
// Mismo patron que `unaSolaAutoridadDePrecio.test.js` aplico al catalogo: el respaldo no se
// borra, se le quita la autoridad sobre el numero.
export const DEFAULT_PACKAGE_RULES = {
  'Starter':           { label: 'Starter',          includedApplications: 3,  suggestedApplicationIds: BRANDING_APPLICATIONS_3,  clientBusinessDays: 15, internalBusinessDays: 12, defaultService: 'Branding',              price_respaldo: 1500 },
  'Select':            { label: 'Select',           includedApplications: 5,  suggestedApplicationIds: BRANDING_APPLICATIONS_5,  clientBusinessDays: 20, internalBusinessDays: 16, defaultService: 'Branding',              price_respaldo: 2900 },
  'Premium':           { label: 'Premium',          includedApplications: 10, suggestedApplicationIds: BRANDING_APPLICATIONS_10, clientBusinessDays: 30, internalBusinessDays: 25, defaultService: 'Branding',              price_respaldo: 4600 },
  'Essence':           { label: 'Essence',          includedApplications: 3,  suggestedApplicationIds: BRANDING_APPLICATIONS_3,  clientBusinessDays: 30, internalBusinessDays: 24, defaultService: 'Branding Corporativo',  price_respaldo: 7400 },
  'Legacy':            { label: 'Legacy',           includedApplications: 5,  suggestedApplicationIds: BRANDING_APPLICATIONS_5,  clientBusinessDays: 40, internalBusinessDays: 32, defaultService: 'Branding Corporativo',  price_respaldo: 9100 },
  'Ascend':            { label: 'Ascend',           includedApplications: 10, suggestedApplicationIds: BRANDING_APPLICATIONS_10, clientBusinessDays: 55, internalBusinessDays: 44, defaultService: 'Branding Corporativo',  price_respaldo: 11700 },
  // Historical aliases.
  'Brand Starter':     { label: 'Brand Starter',    includedApplications: 3,  suggestedApplicationIds: BRANDING_APPLICATIONS_3,  clientBusinessDays: 15, internalBusinessDays: 12, defaultService: 'Branding',              price_respaldo: 1200 },
  'Brand Identity':    { label: 'Brand Identity',   includedApplications: 5,  suggestedApplicationIds: BRANDING_APPLICATIONS_5,  clientBusinessDays: 20, internalBusinessDays: 16, defaultService: 'Branding',              price_respaldo: 2800 },
  'Brand Legacy':      { label: 'Brand Legacy',     includedApplications: 10, suggestedApplicationIds: BRANDING_APPLICATIONS_10, clientBusinessDays: 30, internalBusinessDays: 25, defaultService: 'Branding',              price_respaldo: 4200 },
  'B&L Esencial':      { label: 'B&L Esencial',     includedApplications: 3,  suggestedApplicationIds: BRANDING_APPLICATIONS_3,  clientBusinessDays: 25, internalBusinessDays: 18, defaultService: 'Brand & Legacy',        price_respaldo: 1500 },
  'B&L Signature':     { label: 'B&L Signature',    includedApplications: 5,  suggestedApplicationIds: BRANDING_APPLICATIONS_5,  clientBusinessDays: 40, internalBusinessDays: 30, defaultService: 'Brand & Legacy',        price_respaldo: 2500 },
  'B&L Legacy':        { label: 'B&L Legacy',       includedApplications: 10, suggestedApplicationIds: BRANDING_APPLICATIONS_10, clientBusinessDays: 55, internalBusinessDays: 42, defaultService: 'Brand & Legacy',        price_respaldo: 3800 },
  'Dirección de Arte': { label: 'Dirección de Arte', includedApplications: 0, clientBusinessDays: 10, internalBusinessDays: 7,  defaultService: 'Dirección de Arte',     price_respaldo: 1250 },
  'Naming':            { label: 'Naming',            includedApplications: 0, clientBusinessDays: 10, internalBusinessDays: 7,  defaultService: 'Naming',                price_respaldo: 900  },
  'Web':               { label: 'Web',               includedApplications: 3, clientBusinessDays: 30, internalBusinessDays: 22, defaultService: 'Web',                   price_respaldo: 2400 },
};

const packageConfig = readPackageConfig(DEFAULT_APPLICATION_CATALOG, DEFAULT_PACKAGE_RULES);
export const APPLICATION_CATALOG = packageConfig.applications;
export const PACKAGE_RULES = packageConfig.rules;

export function getPackageRule(packageName = 'Starter') {
  return PACKAGE_RULES[packageName] || PACKAGE_RULES['Starter'];
}

function businessDaysFromDuration(duration = '') {
  const match = String(duration || '').match(/\d+/);
  return match ? Number(match[0]) : null;
}

function isoDate(dateLike = new Date()) {
  const date = dateLike instanceof Date ? new Date(dateLike.getTime()) : new Date(dateLike || Date.now());
  if (Number.isNaN(date.getTime())) return isoDate(new Date());
  date.setHours(12, 0, 0, 0);
  return date.toISOString().slice(0, 10);
}

export function addBusinessDays(startDate = new Date(), businessDays = 0) {
  const days = Math.max(0, Number(businessDays) || 0);
  const date = new Date(isoDate(startDate) + 'T12:00:00');
  let added = 0;

  // Sprint 3.1 excludes weekends. A holiday calendar can plug into this loop later.
  while (added < days) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }

  return isoDate(date);
}

export function businessDaysBetween(startDate = new Date(), endDate = new Date()) {
  const start = new Date(isoDate(startDate) + 'T12:00:00');
  const end = new Date(isoDate(endDate) + 'T12:00:00');
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  if (end < start) return -businessDaysBetween(end, start);

  let count = 0;
  const cursor = new Date(start.getTime());
  while (cursor < end) {
    cursor.setDate(cursor.getDate() + 1);
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count += 1;
  }
  return count;
}

export function businessDaysUntil(dateLike, fromDate = new Date()) {
  if (!dateLike) return null;
  const target = new Date(dateLike);
  if (Number.isNaN(target.getTime())) return null;
  return businessDaysBetween(fromDate, target);
}

export function calculateProjectTimeline(packageName = 'Starter', startDate = new Date()) {
  const rule = getPackageRule(packageName);
  const packageLabel = rule.label || packageName || 'Starter';
  const fallbackDays = businessDaysFromDuration(PAQUETES.find(p => p.nombre === packageLabel)?.duracion) || 15;
  const clientBusinessDays = Math.max(1, Number(rule.clientBusinessDays || rule.businessDays || fallbackDays) || fallbackDays);
  const internalBusinessDays = Math.max(1, Math.min(
    clientBusinessDays,
    Number(rule.internalBusinessDays || Math.round(clientBusinessDays * 0.8)) || Math.round(clientBusinessDays * 0.8)
  ));

  return {
    packageName: packageLabel,
    // ── EL CUPO NO ES UN DATO DE CALENDARIO ────────────────────────────────
    //
    // Esto se escribe en `proyectos.package_app_limit`, asi que es cupo COMERCIAL. Salia de
    // `rule.includedApplications`, y `getPackageRule` devuelve la regla de Starter —cupo 3—
    // cuando el nombre no se reconoce: un helper de fechas decidia un cobro.
    //
    // No se resuelve aqui porque importar `cupoDePaquete` en este modulo haria un ciclo
    // (`packages -> cupoDePaquete -> appCatalog -> packages`) y este fichero tiene
    // inicializacion a nivel de modulo. Se devuelve `null` y lo resuelve quien ESCRIBE,
    // que es quien tiene el catalogo canonico cargado.
    packageAppLimit: null,
    clientBusinessDays,
    internalBusinessDays,
    clientDueDate: addBusinessDays(startDate, clientBusinessDays),
    internalDueDate: addBusinessDays(startDate, internalBusinessDays),
  };
}

export function applicationById(id) {
  return APPLICATION_CATALOG.find(app => app.id === id) || { id, name: id, category: 'Aplicación', basePrice: 0 };
}

export function defaultApplicationsForPackage(packageName) {
  const rule = getPackageRule(packageName);
  // ── LA LISTA SUGERIDA YA TIENE LA LONGITUD DEL CUPO ─────────────────────
  //
  // `BRANDING_APPLICATIONS_3`, `_5` y `_10` tienen exactamente 3, 5 y 10 ids: medido. Asi
  // que recortar la lista por `rule.includedApplications` era redundante, y con un nombre
  // que `getPackageRule` no reconoce el recorte usaba el cupo de Starter.
  //
  // Esto SUGIERE, no cobra: todas las filas salen `is_extra: false`. El cupo comercial del
  // proyecto lo resuelve quien lo escribe, con `cupoCanonicoONull`.
  const suggestedIds = Array.isArray(rule.suggestedApplicationIds) && rule.suggestedApplicationIds.length
    ? rule.suggestedApplicationIds
    : [];
  const suggestedApps = suggestedIds
    .map(id => APPLICATION_CATALOG.find(app => app.id === id))
    .filter(Boolean);

  return suggestedApps.map((app, index) => ({
    application_id: app.id,
    name:           app.name,
    category:       app.category,
    status:         'pendiente',
    is_extra:       false,
    extra_status:   'incluida',
    sort_order:     index + 1,
  }));
}
